import {
  capitalize,
  Checkbox,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import {
  EMPTY_PTO_REQUIREMENTS,
  ImplementArchetypeViewModel,
} from "../ImplementArchetypeViewModel";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { MuiErrorMessage } from "common/components/MuiErrorMessage";
import { PtoSpeed, PtoType } from "protos/atk/enums";
import {
  ptoSpeedToLocaleString,
  ptoTypeToLocationString,
} from "common/utils/enumTranslations";
import { typedPath } from "typed-path";
import { useFormikContext } from "formik";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import React, { FC, useState } from "react";

const S = typedPath<ImplementArchetypeViewModel>();

export const PtoStep: FC<{ disabled?: boolean }> = ({ disabled }) => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const formikProps = useFormikContext<ImplementArchetypeViewModel>();

  const [checkedOverrideBox, setCheckedOverrideBox] = useState<boolean>(
    formikProps.values.ptoRequirements.type !== PtoType.PTO_TYPE_NONE
  );

  const hasPtoRequirements =
    formikProps.values.ptoRequirements.type !== PtoType.PTO_TYPE_NONE ||
    checkedOverrideBox;

  return (
    <Paper className="flex flex-col gap-2 p-4">
      <FormControlLabel
        checked={hasPtoRequirements}
        disabled={disabled}
        onChange={() => {
          if (hasPtoRequirements) {
            setCheckedOverrideBox(false);
            formikProps.setFieldValue(
              S.ptoRequirements.$path,
              EMPTY_PTO_REQUIREMENTS
            );
          } else {
            setCheckedOverrideBox(true);
            formikProps.setFieldValue(S.ptoRequirements.$path, {});
          }
        }}
        control={<Checkbox />}
        label={t("views.autotractor.equipment.implements.hasPtoRequirements")}
      />
      <div className="flex flex-col">
        <Typography>{capitalize(t("utils.descriptors.type_one"))}</Typography>
        <FormControl disabled={!hasPtoRequirements || disabled}>
          <RadioGroup
            className="flex gap-2"
            row
            value={formikProps.values.ptoRequirements.type}
            onChange={(e) =>
              formikProps.setFieldValue(
                S.ptoRequirements.type.$path,
                Number.parseInt(e.target.value)
              )
            }
          >
            <FormControlLabel
              value={PtoType.PTO_TYPE_FRONT}
              control={<Radio />}
              label={capitalize(
                t(ptoTypeToLocationString(PtoType.PTO_TYPE_FRONT))
              )}
            />
            <FormControlLabel
              value={PtoType.PTO_TYPE_REAR}
              control={<Radio />}
              label={capitalize(
                t(ptoTypeToLocationString(PtoType.PTO_TYPE_REAR))
              )}
            />
            <FormControlLabel
              value={PtoType.PTO_TYPE_FRONT_AND_REAR}
              control={<Radio />}
              label={capitalize(
                t(ptoTypeToLocationString(PtoType.PTO_TYPE_FRONT_AND_REAR))
              )}
            />
          </RadioGroup>
        </FormControl>
        <MuiErrorMessage name={S.ptoRequirements.ptoSpeed.$path} />
      </div>
      <div className="flex flex-col">
        <Typography>{capitalize(t("utils.descriptors.speed_one"))}</Typography>
        <FormControl disabled={!hasPtoRequirements || disabled}>
          <RadioGroup
            className="flex gap-2"
            row
            value={formikProps.values.ptoRequirements.ptoSpeed}
            onChange={(e) =>
              formikProps.setFieldValue(
                S.ptoRequirements.ptoSpeed.$path,
                Number.parseInt(e.target.value)
              )
            }
          >
            <FormControlLabel
              value={PtoSpeed.PTO_SPEED_540E}
              control={<Radio />}
              label={t(ptoSpeedToLocaleString(PtoSpeed.PTO_SPEED_540E))}
            />
            <FormControlLabel
              value={PtoSpeed.PTO_SPEED_540}
              control={<Radio />}
              label={t(ptoSpeedToLocaleString(PtoSpeed.PTO_SPEED_540))}
            />
            <FormControlLabel
              value={PtoSpeed.PTO_SPEED_1000E}
              control={<Radio />}
              label={t(ptoSpeedToLocaleString(PtoSpeed.PTO_SPEED_1000E))}
            />
            <FormControlLabel
              value={PtoSpeed.PTO_SPEED_1000}
              control={<Radio />}
              label={t(ptoSpeedToLocaleString(PtoSpeed.PTO_SPEED_1000))}
            />
          </RadioGroup>
        </FormControl>
        <MuiErrorMessage name={S.ptoRequirements.type.$path} />
      </div>
      <FormikDimensionedNumberInput
        name={S.ptoRequirements.engineSpeedRpm.$path}
        label={capitalize(t("models.equipment.engineSpeed"))}
        dimensionedNumberInputProps={{
          storedUnit: "rpm",
          displayUnits: { imperial: "rpm", metric: "rpm" },
          localSystem: measurementSystem,
          disabled: !hasPtoRequirements || disabled,
        }}
      />
    </Paper>
  );
};
